import { useState, useEffect, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useRefreshEffect } from "@/context/RefreshContext";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { History, Search, Inbox, Lock, X, ChevronDown, ChevronRight } from "lucide-react";
import { ORTUNG_COLOR } from "@/lib/constants";

const ORT_BADGE = {
  green: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  yellow: "text-yellow-400 border-yellow-500/40 bg-yellow-500/10",
  red: "text-red-400 border-red-500/40 bg-red-500/10",
  state: "text-slate-300 border-slate-400/40 bg-slate-500/20",
};

const formatDateTime = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d)) return v;
  return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function ParoleLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [nameFilter, setNameFilter] = useState(null);
  const [showOld, setShowOld] = useState(false);
  const [rangeDays, setRangeDays] = useState(7);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/parole/ortung-log");
      setRows(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useRefreshEffect(load);

  const filtered = rows.filter((r) => {
    if (nameFilter) return r.name === nameFilter;
    if (!search.trim()) return true;
    return [r.name, r.ortung, r.officer].some((v) => String(v ?? "").toLowerCase().includes(search.toLowerCase()));
  });

  const total = filtered.length;
  const countColor = (c) => filtered.filter((r) => ORTUNG_COLOR[r.ortung] === c).length;
  const pct = (n) => {
    if (!total) return "0";
    const p = (n / total) * 100;
    if (p > 0 && p < 1) return p.toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 4 });
    return p.toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  };
  const stats = [
    { key: "total", label: "Ortungen insgesamt", value: total, sub: nameFilter ? nameFilter : "Gesamt", box: "text-zinc-100 border-zinc-700 bg-zinc-800/30", num: "text-zinc-100" },
    { key: "green", label: "Positive Ortungen (Grün)", value: countColor("green"), sub: `${pct(countColor("green"))} %`, box: "border-emerald-500/40 bg-emerald-500/5", num: "text-emerald-400" },
    { key: "yellow", label: "Handy aus (Gelb)", value: countColor("yellow"), sub: `${pct(countColor("yellow"))} %`, box: "border-yellow-500/40 bg-yellow-500/5", num: "text-yellow-400" },
    { key: "red", label: "Negative Ortung (Rot)", value: countColor("red"), sub: `${pct(countColor("red"))} %`, box: "border-red-500/40 bg-red-500/5", num: "text-red-400" },
  ];

  // Einträge älter als N Tage einklappen (zählen aber weiterhin in der Statistik oben).
  const collapseActive = rangeDays !== "all";
  const cutoff = Date.now() - Number(rangeDays) * 24 * 60 * 60 * 1000;
  const rowTime = (r) => {
    const d = new Date(r.ortung_zeit || r.created_at);
    return isNaN(d) ? null : d.getTime();
  };
  const recentRows = filtered.filter((r) => { if (!collapseActive) return true; const t = rowTime(r); return t === null || t >= cutoff; });
  const oldRows = collapseActive ? filtered.filter((r) => { const t = rowTime(r); return t !== null && t < cutoff; }) : [];

  const renderRow = (r, dim = false) => {
    const oc = ORTUNG_COLOR[r.ortung];
    return (
      <TableRow key={r.id} data-testid="parolelog-row" className={`border-zinc-800 hover:bg-zinc-900/50 transition-colors duration-150 ${dim ? "opacity-60" : ""}`}>
        <TableCell className="text-sm">
          <button
            data-testid="parolelog-name-btn"
            onClick={() => { setNameFilter(r.name); setSearch(""); }}
            className="text-zinc-200 font-medium hover:text-primary hover:underline transition-colors text-left"
            title="Nur diesen Verlauf anzeigen"
          >
            {r.name || "—"}
          </button>
        </TableCell>
        <TableCell>
          <Badge className={`rounded-sm border font-mono text-[11px] gap-1.5 ${ORT_BADGE[oc] || "text-zinc-400 border-zinc-700 bg-zinc-800/40"}`}>
            {r.ortung === "State" && <Lock className="h-3 w-3" />}{r.ortung || "—"}
          </Badge>
        </TableCell>
        <TableCell className="font-mono text-sm text-zinc-300 whitespace-nowrap">{formatDateTime(r.ortung_zeit || r.created_at)}</TableCell>
        <TableCell className="font-mono text-sm text-zinc-400">{r.officer || "—"}</TableCell>
      </TableRow>
    );
  };

  return (
    <div>
      <PageHeader
        eyebrow="SADCR · Fahndung"
        title="Parole-Log"
        description="Verlauf aller Ortungs-Auswahlen (Grün / Gelb / Rot) pro Parole."
        icon={History}
      />
      <div className="p-8 space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <Input
              data-testid="parolelog-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={!!nameFilter}
              placeholder="Nach Name, Ortung oder Officer suchen…"
              className="pl-9 bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary disabled:opacity-50"
            />
          </div>
          {nameFilter && (
            <button
              data-testid="parolelog-clear-filter"
              onClick={() => setNameFilter(null)}
              className="flex items-center gap-2 h-9 px-3 rounded-sm border border-primary/50 bg-primary/10 text-primary text-sm font-medium hover:bg-primary/20 transition-colors"
            >
              Verlauf: {nameFilter}
              <X className="h-4 w-4" />
            </button>
          )}
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500">Einklappen ab</span>
            <select
              data-testid="parolelog-range-select"
              value={rangeDays}
              onChange={(e) => setRangeDays(e.target.value === "all" ? "all" : Number(e.target.value))}
              className="h-9 rounded-sm border border-zinc-700 bg-transparent px-2 text-sm text-zinc-200 focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option className="bg-[#0C0E12]" value={7}>7 Tage</option>
              <option className="bg-[#0C0E12]" value={30}>30 Tage</option>
              <option className="bg-[#0C0E12]" value={90}>90 Tage</option>
              <option className="bg-[#0C0E12]" value="all">Alles zeigen</option>
            </select>
          </div>
        </div>

        <div data-testid="parolelog-stats" className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {stats.map((s) => (
            <div
              key={s.key}
              data-testid={`parolelog-stat-${s.key}`}
              className={`rounded-sm border p-4 ${s.box}`}
            >
              <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500">{s.label}</div>
              <div className={`mt-2 font-mono text-3xl font-black leading-none ${s.num}`}>{s.value}</div>
              <div className="mt-1 font-mono text-xs text-zinc-500">{s.sub}</div>
            </div>
          ))}
        </div>

        <div className="border border-zinc-800 rounded-sm bg-[#0C0E12] overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500 h-11">Name</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500">Ortung</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500">Zeitpunkt</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500">Officer</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableCell colSpan={4} className="text-center py-10 text-zinc-500 font-mono text-sm">Wird geladen…</TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableCell colSpan={4} className="text-center py-14">
                    <div className="flex flex-col items-center gap-2 text-zinc-600">
                      <Inbox className="h-8 w-8" />
                      <span className="font-mono text-sm">Noch keine Ortungen protokolliert</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {recentRows.map((r) => renderRow(r))}
                  {oldRows.length > 0 && (
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableCell colSpan={4} className="p-0">
                        <button
                          data-testid="parolelog-toggle-old"
                          onClick={() => setShowOld((v) => !v)}
                          className="w-full flex items-center justify-center gap-2 py-3 text-xs font-mono uppercase tracking-wide text-zinc-400 hover:text-primary hover:bg-zinc-900/40 transition-colors"
                        >
                          {showOld ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          {showOld
                            ? "Ältere Einträge ausblenden"
                            : `${oldRows.length} ältere ${oldRows.length === 1 ? "Eintrag" : "Einträge"} (älter als ${rangeDays} Tage) anzeigen`}
                        </button>
                      </TableCell>
                    </TableRow>
                  )}
                  {showOld && oldRows.map((r) => renderRow(r, true))}
                </>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
